import { Button } from "components/ui/Button";
import { StatusBadge } from "components/ui/StatusBadge";
import type { DeliveryItem } from "hooks/use-delivery-api";
import { cn } from "lib/utils";
import { ChevronDown, ChevronRight, Megaphone } from "lucide-react";
import { type FC, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { deliveryBackState } from "routes/DeliveryPage/delivery-back";
import { DELIVERY_STATUS_BADGE } from "routes/DeliveryPage/delivery-status-badge";
import { formatShortDateTime } from "utils/relative-time";

export type DeliveriesGroupedViewProps = {
  items: DeliveryItem[];
};

type Group = {
  seriesId: string | null;
  seriesName: string;
  items: DeliveryItem[];
  latestSentAt: string | null;
};

/**
 * 配信一覧をシリーズごとに折り畳んで表示する.
 * シリーズ未所属の配信は「単発配信」セクションに集約する.
 * @param {DeliveriesGroupedViewProps} props 配信アイテム
 * @return {JSX.Element} グループ表示
 */
export const DeliveriesGroupedView: FC<DeliveriesGroupedViewProps> = ({
  items,
}) => {
  const groups = useMemo(() => buildGroups(items), [items]);
  if (groups.length === 0) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white shadow-sm py-8 text-center text-base-content/60">
        該当する配信がありません
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {groups.map(group => (
        <SeriesGroupSection
          key={group.seriesId ?? "__single__"}
          group={group}
        />
      ))}
    </div>
  );
};

type SeriesGroupSectionProps = {
  group: Group;
};

const SeriesGroupSection: FC<SeriesGroupSectionProps> = ({ group }) => {
  const [expanded, setExpanded] = useState(true);
  const isSingle = group.seriesId === null;
  return (
    <section className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
      <header
        className={cn(
          "flex items-center gap-2 px-4 py-3 border-b border-slate-200",
          isSingle ? "bg-slate-50" : "bg-violet-50",
        )}
      >
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          className="p-0.5 rounded hover:bg-black/5"
          aria-label={expanded ? "折り畳み" : "展開"}
        >
          {expanded ? (
            <ChevronDown size={14} aria-hidden />
          ) : (
            <ChevronRight size={14} aria-hidden />
          )}
        </button>
        {!isSingle && (
          <Megaphone size={14} className="text-violet-700" aria-hidden />
        )}
        <h3
          className={cn(
            "text-sm font-semibold",
            isSingle ? "text-base-content" : "text-violet-900",
          )}
        >
          {isSingle ? (
            group.seriesName
          ) : (
            <Link
              to={`/delivery-series/${group.seriesId}`}
              className="hover:underline"
            >
              {group.seriesName}
            </Link>
          )}
        </h3>
        <span className="text-xs text-base-content/60">
          {isSingle ? "シリーズ未所属" : "シリーズ"}
        </span>
        <span className="ml-auto text-xs px-2 py-0.5 rounded-full bg-white border border-slate-200 text-base-content/70">
          配信 {group.items.length} 件
        </span>
      </header>
      {expanded && (
        <ul>
          {group.items.map(item => (
            <DeliveryRow key={item.id} item={item} />
          ))}
        </ul>
      )}
    </section>
  );
};

type DeliveryRowProps = {
  item: DeliveryItem;
};

const DeliveryRow: FC<DeliveryRowProps> = ({ item }) => {
  const status = DELIVERY_STATUS_BADGE[item.status];
  const sentAt = item.sentAt ? formatShortDateTime(item.sentAt) : null;
  // 配信詳細の「戻る」がグループ表示のままの一覧へ戻れるよう、リンクに載せる
  const backState = deliveryBackState(useLocation());
  return (
    <li className="px-4 py-3 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
      <Link
        to={`/deliveries/${item.id}`}
        state={backState}
        className="flex items-center gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium truncate">
            {item.subject || "(件名なし)"}
          </div>
          <div className="text-xs text-base-content/60 mt-0.5 flex flex-wrap gap-x-3">
            <span>{item.groupName ?? "宛先未指定"}</span>
            <span>{item.totalRecipients} 名宛</span>
            {sentAt && <span>配信 {sentAt}</span>}
          </div>
        </div>
        {status && <StatusBadge tone={status.tone}>{status.label}</StatusBadge>}
        <Button
          type="button"
          size="xs"
          variant="outline"
          onClick={e => {
            e.preventDefault();
            window.location.href = `/deliveries/new?copy-from=${item.id}`;
          }}
        >
          再配信
        </Button>
      </Link>
    </li>
  );
};

/**
 * 配信群をシリーズごとに集約する. シリーズ未所属は最後の「単発配信」グループに集約.
 * 各グループ内は受信日時降順. グループ自体はシリーズの最終配信日時降順 (ただし単発は最下).
 * @param {DeliveryItem[]} items 配信群
 * @return {Group[]} グループ
 */
function buildGroups(items: DeliveryItem[]): Group[] {
  const map = new Map<string, Group>();
  const SINGLE_KEY = "__single__";

  for (const item of items) {
    const key = item.seriesId ?? SINGLE_KEY;
    let group = map.get(key);
    if (!group) {
      group = {
        seriesId: item.seriesId,
        seriesName: item.seriesName ?? "単発配信",
        items: [],
        latestSentAt: null,
      };
      map.set(key, group);
    }
    group.items.push(item);
    if (item.sentAt) {
      if (!group.latestSentAt || item.sentAt > group.latestSentAt) {
        group.latestSentAt = item.sentAt;
      }
    }
  }

  // 各グループの中はソート (sentAt 降順 → createdAt 降順 fallback)
  for (const group of map.values()) {
    group.items.sort((a, b) => {
      const aKey = a.sentAt ?? a.createdAt;
      const bKey = b.sentAt ?? b.createdAt;
      return bKey.localeCompare(aKey);
    });
  }

  // グループソート: 単発は最下に固定。 それ以外は最終配信日時降順
  return Array.from(map.values()).sort((a, b) => {
    if (a.seriesId === null) return 1;
    if (b.seriesId === null) return -1;
    const aKey = a.latestSentAt ?? "";
    const bKey = b.latestSentAt ?? "";
    return bKey.localeCompare(aKey);
  });
}
